import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BookOpenText, Search, Pin, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { GlassCard, SectionLabel, GradientButton } from "@/components/primitives";
import Markdown from "@/components/Markdown";

export default function Knowledge() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [active, setActive] = useState(null);
  const [askQ, setAskQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);

  const categories = ["all", ...Array.from(new Set(list.map((a) => a.category)))];

  const load = async () => {
    const params = {}; if (q) params.q = q; if (cat !== "all") params.category = cat;
    const { data } = await api.get("/kb", { params });
    setList(data);
    if (!active && data.length) setActive(data[0]);
  };
  useEffect(() => { load(); }, [q, cat]); // eslint-disable-line

  const ask = async () => {
    if (!askQ.trim()) return;
    setBusy(true); setAnswer("");
    try {
      const { data } = await api.post("/kb/ask", { question: askQ });
      setAnswer(data.answer);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="knowledge-page">
      <div>
        <SectionLabel>Knowledge Base</SectionLabel>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">Search every <span className="gradient-text">runbook, SOP & policy</span></h1>
      </div>

      <GlassCard className="p-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
            <input data-testid="kb-ask-input" value={askQ} onChange={(e) => setAskQ(e.target.value)} placeholder="Ask any policy or SOP question…" className="w-full pl-9 pr-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-xl text-sm focus:outline-none focus:border-cyan-400/50" />
          </div>
          <GradientButton onClick={ask} disabled={busy} data-testid="kb-ask-submit"><Sparkles className="h-4 w-4" /> {busy ? "Thinking…" : "Ask AI"}</GradientButton>
        </div>
        {answer && (
          <div className="mt-4 p-4 rounded-xl bg-cyan-500/[0.04] border border-cyan-500/20" data-testid="kb-answer">
            <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mb-1">AI Answer</div>
            <Markdown source={answer} />
          </div>
        )}
      </GlassCard>

      <div className="grid lg:grid-cols-[300px_1fr] gap-5">
        <GlassCard className="p-3 max-h-[70vh] overflow-auto">
          <div className="p-2 flex items-center gap-2">
            <Search className="h-3.5 w-3.5 text-slate-500" />
            <input data-testid="kb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter articles" className="flex-1 bg-transparent text-sm outline-none" />
          </div>
          <div className="flex flex-wrap gap-1 p-2">
            {categories.map((c) => (
              <button data-testid={`kb-cat-${c}`} key={c} onClick={() => setCat(c)} className={`text-[10px] uppercase tracking-wider font-mono px-2 py-1 rounded-md border ${cat === c ? "bg-cyan-500/10 text-cyan-200 border-cyan-500/30" : "bg-white/[0.03] text-slate-400 border-white/10"}`}>{c}</button>
            ))}
          </div>
          <div className="mt-2">
            {list.map((a) => (
              <button key={a.id} onClick={() => setActive(a)} className={`w-full text-left p-3 rounded-xl mb-1 ${active?.id === a.id ? "bg-white/[0.06] border border-white/10" : "hover:bg-white/[0.04]"}`}>
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">{a.pinned && <Pin className="h-3 w-3 text-cyan-300" />}{a.category}</div>
                <div className="text-sm font-semibold truncate">{a.title}</div>
              </button>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-7">
          {active ? (
            <motion.div key={active.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">{active.category}</div>
              <h2 className="mt-1 text-2xl font-bold">{active.title}</h2>
              <div className="text-[11px] text-slate-500 font-mono mt-1">By {active.author} · {active.views} views</div>
              <div className="mt-5"><Markdown source={active.content} /></div>
            </motion.div>
          ) : <div className="text-slate-400">Select an article.</div>}
        </GlassCard>
      </div>
    </div>
  );
}
